import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { displayUrl, languageTag, type AppEntry } from "../registry/apps";
import { posterFor } from "../registry/posters";
import { isLive } from "../lib/progress";
import { sheetShouldClose, swipeStep } from "../lib/gestures";

type AppModalProps = {
  app: AppEntry;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
};

/** Arrow keys belong to text fields while one has focus. */
function isTyping(el: Element | null): boolean {
  return (
    el instanceof HTMLElement &&
    (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
  );
}

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Break a trailing parenthetical onto its own line so a long bilingual title can wrap. */
function ModalTitle({ title }: { title: string }) {
  const cut = title.lastIndexOf(" (");
  if (cut < 0 || !title.endsWith(")")) return title;
  return (
    <>
      {title.slice(0, cut)}{" "}
      <span className="app-modal-sub">{title.slice(cut + 1)}</span>
    </>
  );
}

export function AppModal({ app, onClose, onPrev, onNext }: AppModalProps) {
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragYRef = useRef(0);
  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const href = displayUrl(app);
  const live = isLive(app);
  const blurb = app.blurb?.trim() || "Live on the over9000.cc path door.";
  const tag = languageTag(app.language);
  const poster = posterFor(app.slug);
  const titleId = "app-modal-title";
  const blurbId = "app-modal-blurb";
  const langId = "app-modal-lang";

  useLayoutEffect(() => {
    const previouslyFocused = document.activeElement;
    if (!dialogRef.current?.contains(document.activeElement)) closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (
        (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
        !isTyping(document.activeElement)
      ) {
        event.preventDefault();
        event.stopPropagation();
        (event.key === "ArrowLeft" ? onPrev : onNext)();
        return;
      }
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Native, non-passive touch listeners: a horizontal swipe steps apps and must
    // preventDefault so the browser doesn't treat it as a history-back swipe.
    const dialog = dialogRef.current;
    const onTouchStart = (event: TouchEvent) => {
      const t = event.touches[0];
      swipeRef.current = event.touches.length === 1 && t ? { x: t.clientX, y: t.clientY } : null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const start = swipeRef.current;
      const t = event.touches[0];
      if (!start || !t) return;
      const dx = Math.abs(t.clientX - start.x);
      if (dx > 10 && dx > Math.abs(t.clientY - start.y) && event.cancelable) event.preventDefault();
    };
    const onTouchEnd = (event: TouchEvent) => {
      const start = swipeRef.current;
      const t = event.changedTouches[0];
      swipeRef.current = null;
      if (!start || !t) return;
      const step = swipeStep(t.clientX - start.x, t.clientY - start.y);
      if (step) (step < 0 ? onPrev : onNext)();
    };
    dialog?.addEventListener("touchstart", onTouchStart, { passive: true });
    dialog?.addEventListener("touchmove", onTouchMove, { passive: false });
    dialog?.addEventListener("touchend", onTouchEnd);

    window.addEventListener("keydown", onKey);
    return () => {
      dialog?.removeEventListener("touchstart", onTouchStart);
      dialog?.removeEventListener("touchmove", onTouchMove);
      dialog?.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKey);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const onHandleDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    startYRef.current = event.clientY;
    dragYRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHandleMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || !dialogRef.current) return;
    const dy = Math.max(0, event.clientY - startYRef.current);
    dragYRef.current = dy;
    dialogRef.current.style.transform = `translateY(${dy}px)`;
  };

  const onHandleUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (sheetShouldClose(dragYRef.current)) {
      onClose();
      return;
    }
    if (dialogRef.current) dialogRef.current.style.transform = "";
  };

  return (
    <div
      className="app-modal-back"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="app-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={tag ? `${titleId} ${langId}` : titleId}
        aria-describedby={blurbId}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="sheet-handle"
          aria-hidden="true"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
        />

        <header className="app-modal-head">
          <p className="app-modal-kicker">
            <span>{app.surface}</span>
            {tag ? (
              <span id={langId} className="pill lang">
                {tag}
              </span>
            ) : null}
            <span className={`pill ${live ? "ok" : "warn"}`}>
              {live ? "LIVE" : app.example ? "EXAMPLE" : "WIP"}
            </span>
          </p>
          <h2 id={titleId}>
            <ModalTitle title={app.title} />
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="app-modal-close"
            onClick={onClose}
            aria-label="Close details"
          >
            CLOSE
          </button>
        </header>

        <div className="app-modal-body">
          <div className={poster ? "app-modal-media has-poster" : "app-modal-media"}>
            {poster ? (
              <div className="app-modal-poster-frame">
                <img
                  className="app-modal-poster"
                  src={poster}
                  alt={`${app.title} poster`}
                  width={720}
                  height={1280}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            <img
              className="app-modal-shot"
              src={app.screenshot}
              alt={`${app.title} screenshot`}
              width={1440}
              height={900}
            />
          </div>
          <p id={blurbId} className="app-modal-blurb">
            {blurb}
          </p>
          <div className="app-modal-url">
            <p className="app-modal-url-text">{href}</p>
            <button
              type="button"
              className="app-modal-copy"
              onClick={() => void copyUrl()}
              aria-live="polite"
            >
              {copied ? "COPIED" : "COPY"}
            </button>
          </div>
        </div>

        <footer className="app-modal-actions">
          <div className="app-modal-nav">
            <button type="button" className="app-modal-step" onClick={onPrev} aria-label="Previous app">
              ‹ PREV
            </button>
            <button type="button" className="app-modal-step" onClick={onNext} aria-label="Next app">
              NEXT ›
            </button>
          </div>
          {live ? (
            <a
              className="app-open"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              OPEN APP
            </a>
          ) : (
            <p className="app-modal-note">Not live yet — the path door is not open.</p>
          )}
        </footer>
      </section>
    </div>
  );
}
